import React, { useState, useEffect, useRef } from 'react';
import config from '../config/environment';

const Matchmaking = ({ onNavigate }) => {
  const [playerName, setPlayerName] = useState('');
  const [rating, setRating] = useState(1200);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(0);
  const [searchStartTime, setSearchStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const wsRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isSearching && searchStartTime) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - searchStartTime) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSearching, searchStartTime]);

  const connectToMatchmaking = () => {
    const wsUrl = `${config.getWebSocketUrl()}/ws/matchmaking`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('Connected to matchmaking');
    };

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'searchStarted':
          setIsSearching(true);
          setSearchStartTime(Date.now());
          setEstimatedWaitTime(message.estimatedWaitTime);
          setError('');
          break;

        case 'matchFound':
          setIsSearching(false);
          // Navigate to game room
          onNavigate('game', {
            roomId: message.roomId,
            playerName: playerName,
            isMatchmade: true
          });
          break;

        case 'searchCancelled':
          setIsSearching(false);
          setSearchStartTime(null);
          setElapsedTime(0);
          break;

        case 'error':
          setError(message.message);
          setIsSearching(false);
          setSearchStartTime(null);
          setElapsedTime(0);
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    };

    wsRef.current.onerror = (error) => {
      setError('Connection error. Please try again.');
      setIsSearching(false);
      setSearchStartTime(null);
      setElapsedTime(0);
    };

    wsRef.current.onclose = () => {
      if (isSearching) {
        setError('Connection lost. Please try again.');
        setIsSearching(false);
        setSearchStartTime(null);
        setElapsedTime(0);
      }
    };
  };

  const startSearch = () => {
    if (!playerName.trim()) {
      setError('Player name is required');
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectToMatchmaking();
      // Wait for connection to open, then send the message
      const checkConnection = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          clearInterval(checkConnection);
          wsRef.current.send(JSON.stringify({
            type: 'findMatch',
            playerName: playerName.trim(),
            rating: rating
          }));
        }
      }, 100);
    } else {
      wsRef.current.send(JSON.stringify({
        type: 'findMatch',
        playerName: playerName.trim(),
        rating: rating
      }));
    }
  };

  const cancelSearch = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cancelSearch'
      }));
    }
    setIsSearching(false);
    setSearchStartTime(null);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container">
      <div className="card">
        <button className="back-button" onClick={() => onNavigate('home')}>
          ← Back to Home
        </button>
        
        <h2>Quick Match</h2>
        <p>Find players for a quick Ludo game!</p>
        
        {error && <div className="error-message">{error}</div>}
        
        {!isSearching ? (
          <div className="form-container">
            <div className="form-group">
              <label htmlFor="playerName">Your Name:</label>
              <input
                type="text"
                id="playerName"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                maxLength="20"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="rating">Your Rating:</label>
              <input
                type="range"
                id="rating"
                min="800"
                max="2000"
                step="50"
                value={rating}
                onChange={(e) => setRating(parseInt(e.target.value))}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>
                <strong>{rating}</strong>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  (Players with similar ratings will be matched)
                </div>
              </div>
            </div>

            <button
              onClick={startSearch}
              className="primary-button"
              style={{ width: '100%', marginTop: '20px' }}
            >
              🔍 Find Match
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="loading" style={{ marginBottom: '20px' }}></div>
            <h3>🔍 Searching for players...</h3>
            <p>Elapsed time: <strong>{formatTime(elapsedTime)}</strong></p>
            <p>Estimated wait: <strong>{formatTime(estimatedWaitTime)}</strong></p>
            
            <button
              onClick={cancelSearch}
              className="danger-button"
              style={{ marginTop: '20px' }}
            >
              Cancel Search
            </button>
          </div>
        )}

        <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h4>How it works:</h4>
          <ul style={{ textAlign: 'left', margin: '10px 0' }}>
            <li>We'll match you with 1-3 other players</li>
            <li>Players with similar ratings are prioritized</li>
            <li>Games can start with 2-4 players</li>
            <li>Average wait time is 10-30 seconds</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Matchmaking;
